// Diary API — typed wrappers for /api/v1/diaries/* + classify_mood.
//
// W2 §6.3 surface: list / create / read / update / soft-delete / restore
// / trash / classify_mood (Laya-gated).  Privacy is server-enforced via
// the ``include_private`` query flag.

import type { MediaOut as _MediaOutLike } from "./media";
import { api as openapi } from "./client";
import { ApiError } from "./pair";
import { assertResponseBody } from "./emptyBody";
import type {
  ClassifyMoodRequest as SchemaClassifyMoodRequest,
  DiaryImageOut as SchemaDiaryImageOut,
  DiaryMediaAttachRequest as SchemaDiaryMediaAttachRequest,
  DiaryMediaListResponse as SchemaDiaryMediaListResponse,
  DiaryTrashItem as SchemaDiaryTrashItem,
  DiaryTrashResponse as SchemaDiaryTrashResponse,
} from "./schema";
import { API_REQUEST_FAILED } from "../i18n/errors";
import type { z } from "zod";
import type { moodCodeSchema } from "@/lib/validation/diary";

// Re-export for consumers that import from this file.
export type MediaOut = _MediaOutLike;
/** Narrow union of the 30 valid mood codes — derived from the zod schema. */
export type MoodCode = z.infer<typeof moodCodeSchema>;
// Re-export zod-derived input shapes so consumers can keep imports in `lib/api`.
export type { DiaryCreateInput, DiaryUpdateInput, ClassifyMoodInput } from "@/lib/validation/diary";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyClient = Record<"GET" | "POST" | "PUT" | "DELETE" | "PATCH", any>;
const api = openapi as unknown as AnyClient;

interface CallOptions {
  body?: unknown;
  headers?: Record<string, string>;
  /**
   * openapi-fetch reads query parameters from `params.query` (see
   * `DefaultParamsOption` in openapi-fetch's index.d.ts and `createFinalURL`,
   * which calls `querySerializer(options.params.query ?? {})`). A flat
   * `params: { limit }` is therefore **silently dropped** — the request goes
   * out with no query string and the backend falls back to its own defaults.
   * W38 browser audit found exactly that: `/dashboard` asked `listDiaries(null,
   * 3, false)` and rendered 20 cards because `limit=3` never left the browser.
   * Every query-param call site in `lib/api/` must use this shape.
   */
  params?: { query: Record<string, unknown> };
  /**
   * Set `false` only on a route whose OpenAPI contract declares no 2xx body
   * (every one of them answers `204`). A 2xx that arrives empty on any other
   * route is a broken server and must reach `isError` — see `emptyBody.ts`.
   */
  expectBody?: boolean;
}

async function call<T>(
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
  path: string,
  opts: CallOptions = {},
): Promise<{ response: Response; data: T | null; error?: unknown }> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result = await (api as any)[method](path, opts);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data = (result.data as any) ?? null;
  // A bodyless 2xx where the contract declares a body arrives as `null` and
  // every `return res.data as T` below would ship it to a screen. Guard the
  // one funnel rather than each component — see `emptyBody.ts`.
  if (result.response?.ok)
    assertResponseBody(result.response, result.data, opts.expectBody !== false);
  // W39.79. `error` is openapi-fetch's **already-parsed** body for a non-2xx.
  // Without it `apiError` can only try to re-read a body the client consumed.
  return { response: result.response, data, error: (result as { error?: unknown }).error };
}

/**
 * Build an `ApiError` whose message is something a user can read.
 *
 * W39.53 — all fifteen `throw new ApiError(…, res.response.statusText)` sites in
 * this file were unreachable by the copy guard, because it only recognised the
 * `?? res.response.statusText` fallback and a *literal* in the message position,
 * and this file passes `statusText` **positionally**. `call()` does not throw, so
 * the branch is live: a 401 from a revoked session, or a 502 from the reverse
 * proxy during a deploy, put `fetch`'s English reason phrase straight into the
 * diary error screen's description.
 *
 * The documented envelope (`backend/app/core/errors.py`) is
 * `{"error": {"code", "message": "<繁體中文>"}}`, so reading `error.message`
 * gives the server's own Chinese copy and `API_REQUEST_FAILED` covers the case
 * where the body is not JSON at all.
 */
async function apiError(
  res: { response: Response; data: unknown; error?: unknown },
  fallbackCode: string,
): Promise<ApiError> {
  // W39.81 — read openapi-fetch's already-parsed body first. The `Response` in
  // `res.response` has had its body consumed by the client
  // (`dist/index.js:244`), so `clone().json()` cannot read it and every diary
  // error fell through to the fallback. See `album.ts` for the full account,
  // including why W39.79's "repo-wide" claim was five modules short.
  let body: unknown = res.error ?? null;
  if (body === null) {
    try {
      body = await res.response.clone().json();
    } catch {
      body = null;
    }
  }
  const err = (
    body as {
      error?: { code?: string; message?: string; details?: unknown };
    } | null
  )?.error;
  return new ApiError(
    res.response.status,
    err?.code ?? fallbackCode,
    err?.message ?? API_REQUEST_FAILED,
  );
}

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type DiaryImageOut = SchemaDiaryImageOut;

export interface DiaryOut {
  id: string;
  pair_id: string;
  author_id: string;
  /** ISO 8601 date in HKT (per architecture §3). */
  date: string;
  title: string | null;
  content: string;
  /** One of 30 mood codes (locked at schema layer). */
  mood: MoodCode | null;
  is_private: boolean;
  /** W3.1 — source linkage. Currently only `anniversary` is wired. */
  source_type: "anniversary" | null;
  source_id: string | null;
  /** W35 — reverse-link to a wish (set when created via
   *  /api/v1/wishes/{id}/complete-with-diary). */
  source_wish_id: string | null;
  created_at: string;
  updated_at: string;
  images: DiaryImageOut[];
}

export interface DiaryListResponse {
  items: DiaryOut[];
  next_cursor: string | null;
  has_more: boolean;
}

/**
 * One soft-deleted diary as the trash shows it (W39.78).
 *
 * Deliberately **not** a `DiaryOut`: the bin needs a name and a countdown, not
 * the body. `days_left` and `can_restore` are the server's so that the
 * 「N 天後永久刪除」 line, the disabled state of 還原, and the cron that really
 * deletes the row cannot disagree.
 */
export type DiaryTrashItem = SchemaDiaryTrashItem;

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type DiaryTrashResponse = SchemaDiaryTrashResponse;

export interface DiaryCreatePayload {
  /** ISO 8601 date in HKT; must be ≤ today. */
  date: string;
  title?: string | null;
  content: string;
  mood?: MoodCode | null;
  /** Default false (shared). */
  is_private?: boolean;
  /** Media ids to attach (already uploaded via /api/v1/media). */
  media_ids?: string[];
  /**
   * W3.1 added `anniversary`; W50.3 D-3 added `wishlist`.
   *
   * The backend's enum (`diary/schemas.py::DIARY_SOURCE_TYPES`) has carried
   * **both** values since W2 — this type was simply narrower than the contract,
   * so the wish half could not be sent from here even though the endpoint
   * accepted it. Widened to match; the schema-parity test pins the equality.
   */
  source_type?: "anniversary" | "wishlist" | null;
  source_id?: string | null;
}

export interface DiaryUpdatePayload {
  date?: string | null;
  title?: string | null;
  content?: string | null;
  mood?: MoodCode | null;
  is_private?: boolean | null;
  // W49.0 §9. `source_type` / `source_id` used to be declared here. The
  // backend's `DiaryUpdate` is `extra="forbid"` and has neither, so any PATCH
  // that included them is a 422. Nothing fired only because `DiaryEditor`
  // happened to send exactly the five allowed fields — a loaded gun with no
  // one pulling the trigger yet, and the next person to add a field to this
  // interface would have walked straight into it. Removed rather than
  // forwarded: adding the source link is a backend change
  // (`/diary/{id}/source`, currently orphan — see
  // `docs/audit/orphan-endpoints-w49.md`), and it should arrive as a contract
  // change rather than as a type that lies about the contract.
}

export type ClassifyBand = "high" | "mid" | "low" | "reject";

export interface ClassifyMoodResponse {
  mood: MoodCode;
  confidence: number;
  probabilities: Record<string, number>;
  band: ClassifyBand;
}

// ---------------------------------------------------------------------------
// Routes
// ---------------------------------------------------------------------------

export async function listDiaries(
  cursor?: string | null,
  limit = 20,
  includePrivate = false,
  mood?: MoodCode | null,
): Promise<DiaryListResponse> {
  const params: Record<string, unknown> = { limit, include_private: includePrivate };
  if (cursor) params["cursor"] = cursor;
  if (mood) params["mood"] = mood;
  const res = await call<DiaryListResponse>("GET", "/api/v1/diaries", {
    params: { query: params },
  });
  if (!res.response.ok) throw await apiError(res, "DIARY_LIST_FAILED");
  return res.data as DiaryListResponse;
}

export async function createDiary(payload: DiaryCreatePayload): Promise<DiaryOut> {
  const res = await call<DiaryOut>("POST", "/api/v1/diaries", { body: payload });
  if (!res.response.ok) throw await apiError(res, "DIARY_CREATE_FAILED");
  return res.data as DiaryOut;
}

export async function readDiary(diaryId: string): Promise<DiaryOut> {
  const res = await call<DiaryOut>("GET", `/api/v1/diaries/${diaryId}`);
  if (!res.response.ok) throw await apiError(res, "DIARY_READ_FAILED");
  return res.data as DiaryOut;
}

export async function updateDiary(diaryId: string, payload: DiaryUpdatePayload): Promise<DiaryOut> {
  const res = await call<DiaryOut>("PATCH", `/api/v1/diaries/${diaryId}`, { body: payload });
  if (!res.response.ok) throw await apiError(res, "DIARY_UPDATE_FAILED");
  return res.data as DiaryOut;
}

export async function softDeleteDiary(diaryId: string): Promise<void> {
  const res = await call<unknown>("DELETE", `/api/v1/diaries/${diaryId}`, { expectBody: false });
  if (!res.response.ok) throw await apiError(res, "DIARY_DELETE_FAILED");
}

// --- Cross-module source link (W50.3 D-3) ---------------------------------
//
// The only way to attribute a diary to a wish. Until W50.3 nothing in the
// frontend called this at all: `WishDetail.tsx` used a raw `fetch` against the
// *reverse* lookup, which reads what a previous write left behind, and the only
// writer was `complete-with-diary` on the backend. So "連結日記" was not a
// feature with a missing button — it was a feature with no entry point, and a
// backend endpoint that wrote a column the wishlist side never read.

/** The locked pair the backend accepts. `null` clears the link. */
export type DiarySourceType = "anniversary" | "wishlist";

export interface DiarySourceUpdate {
  source_type: DiarySourceType | null;
  source_id: string | null;
}

/**
 * Set or clear the diary's origin.
 *
 * Both fields travel together — the backend rejects one without the other, and
 * sending only `source_type` is a 422 rather than a partial update.
 */
export async function setDiarySource(
  diaryId: string,
  payload: DiarySourceUpdate,
): Promise<DiaryOut> {
  const res = await call<DiaryOut>("PUT", `/api/v1/diaries/${diaryId}/source`, {
    body: payload,
  });
  if (!res.response.ok) throw await apiError(res, "DIARY_SOURCE_UPDATE_FAILED");
  return res.data as DiaryOut;
}

/**
 * Reverse lookup: the diary created from / linked to a source, or `null`.
 *
 * `null` is a real answer, not an error — W39.87 made this route answer 200 with
 * `null` for "this wish has no diary yet" because the 404 was logging a console
 * error every sweep. Do not turn it back into an exception.
 */
export async function findDiaryBySource(
  sourceType: DiarySourceType,
  sourceId: string,
): Promise<DiaryOut | null> {
  const res = await call<DiaryOut | null>(
    "GET",
    `/api/v1/source/${sourceType}/${sourceId}/diary`,
  );
  if (!res.response.ok) throw await apiError(res, "DIARY_SOURCE_LOOKUP_FAILED");
  return (res.data ?? null) as DiaryOut | null;
}

export async function restoreDiary(diaryId: string): Promise<DiaryOut> {
  const res = await call<DiaryOut>("POST", `/api/v1/diaries/${diaryId}/restore`);
  if (!res.response.ok) throw await apiError(res, "DIARY_RESTORE_FAILED");
  return res.data as DiaryOut;
}

export async function listTrash(): Promise<DiaryTrashResponse> {
  const res = await call<DiaryTrashResponse>("GET", "/api/v1/diaries/trash");
  if (!res.response.ok) throw await apiError(res, "DIARY_TRASH_FAILED");
  return res.data as DiaryTrashResponse;
}

/**
 * `DELETE /api/v1/diaries/{id}/permanent` — the irreversible one (W39.78).
 *
 * The counterpart to {@link softDeleteDiary}, and the one the shared `TrashList`
 * calls for 永久刪除. It refuses a row that is not already in the bin, so it
 * cannot be reached by accident from a live diary.
 */
export async function purgeDiary(diaryId: string): Promise<void> {
  const res = await call<unknown>("DELETE", `/api/v1/diaries/${diaryId}/permanent`, {
    expectBody: false,
  });
  if (!res.response.ok) throw await apiError(res, "DIARY_PURGE_FAILED");
}

/** GET /api/v1/moods — returns the locked 30 mood codes. */
export async function listMoods(): Promise<MoodCode[]> {
  const res = await call<MoodCode[]>("GET", "/api/v1/moods");
  if (!res.response.ok) throw await apiError(res, "MOODS_LIST_FAILED");
  return (res.data ?? []) as MoodCode[];
}

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type ClassifyMoodPayload = SchemaClassifyMoodRequest;

/**
 * POST /api/v1/diary/classify_mood — Laya-gated SUGGEST (off by default).
 *
 * Returns a top-1 mood suggestion with calibrated confidence. The server
 * may respond with 503 ``DIARY_FEATURE_TEMPORARILY_UNAVAILABLE`` when the
 * Laya sidecar is unreachable or the ``LAYA_ENABLED`` flag is off (MVP
 * default). Callers should handle that case with a fallback heuristic
 * (e.g. default to ``mood_calm_3``).
 */
export async function classifyMood(payload: ClassifyMoodPayload): Promise<ClassifyMoodResponse> {
  const res = await call<ClassifyMoodResponse>("POST", "/api/v1/diary/classify_mood", {
    body: payload,
  });
  if (!res.response.ok) throw await apiError(res, "DIARY_CLASSIFY_MOOD_FAILED");
  return res.data as ClassifyMoodResponse;
}

// --- Likes (W3 §8) --------------------------------------------------------

export interface DiaryLikeOut {
  diary_id: string;
  user_id: string;
  liked_at: string;
  withdrawn_at: string | null;
}

export interface DiaryLikeListResponse {
  items: DiaryLikeOut[];
  next_cursor: string | null;
  has_more: boolean;
  total: number;
}

/** POST /api/v1/diaries/{id}/likes — like a diary (idempotent). */
export async function likeDiary(diaryId: string): Promise<DiaryLikeOut> {
  const res = await call<DiaryLikeOut>("POST", `/api/v1/diaries/${diaryId}/likes`);
  if (!res.response.ok) throw await apiError(res, "DIARY_LIKE_FAILED");
  return res.data as DiaryLikeOut;
}

/** DELETE /api/v1/diaries/{id}/likes — unlike a diary (idempotent). */
export async function unlikeDiary(diaryId: string): Promise<void> {
  const res = await call<unknown>("DELETE", `/api/v1/diaries/${diaryId}/likes`, {
    expectBody: false,
  });
  if (!res.response.ok) throw await apiError(res, "DIARY_UNLIKE_FAILED");
}

/** GET /api/v1/diaries/{id}/likes — list active likes (cursor paginated). */
export async function listLikes(
  diaryId: string,
  cursor: string | null = null,
  limit = 50,
): Promise<DiaryLikeListResponse> {
  const params: Record<string, unknown> = { limit };
  if (cursor) params["cursor"] = cursor;
  const res = await call<DiaryLikeListResponse>("GET", `/api/v1/diaries/${diaryId}/likes`, {
    params: { query: params },
  });
  if (!res.response.ok) throw await apiError(res, "DIARY_LIKES_LIST_FAILED");
  return (res.data ?? {
    items: [],
    next_cursor: null,
    has_more: false,
    total: 0,
  }) as DiaryLikeListResponse;
}

// --- Diary <-> Media (w3.md §5.1 / §7.2) ------------------------------------
//
// Surfaces the future 「從日記選擇照片」 modal's underlying API:
// the diary detail page lists images via ``DiaryOut.images`` (cached in
// ``useDiary``), and the dedicated endpoints below support attaching
// already-uploaded Media rows to a diary (without re-uploading the bytes)
// and detaching individual DiaryImage rows.  See docs/spec/w3.md §5.1.

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type DiaryMediaListResponse = SchemaDiaryMediaListResponse;

/** GET /api/v1/diaries/{id}/media — list diary images (privacy-checked). */
export async function listDiaryMedia(diaryId: string): Promise<DiaryMediaListResponse> {
  const res = await call<DiaryMediaListResponse>("GET", `/api/v1/diaries/${diaryId}/media`);
  if (!res.response.ok) throw await apiError(res, "DIARY_MEDIA_LIST_FAILED");
  return (res.data ?? { items: [], total: 0 }) as DiaryMediaListResponse;
}

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type DiaryMediaAttachPayload = SchemaDiaryMediaAttachRequest;

/**
 * POST /api/v1/diaries/{id}/media — attach existing Media rows to a diary.
 *
 * Only the diary's author can attach; each ``media_id`` must belong to a
 * Media row uploaded by the same user (server enforces).  Duplicate
 * ``(diary_id, media_id)`` pairs are idempotent — re-attaching a media
 * that's already linked returns the existing row.
 */
export async function attachDiaryMedia(
  diaryId: string,
  payload: DiaryMediaAttachPayload,
): Promise<DiaryMediaListResponse> {
  const res = await call<DiaryMediaListResponse>("POST", `/api/v1/diaries/${diaryId}/media`, {
    body: payload,
  });
  if (!res.response.ok) throw await apiError(res, "DIARY_MEDIA_ATTACH_FAILED");
  return (res.data ?? { items: [], total: 0 }) as DiaryMediaListResponse;
}

/** DELETE /api/v1/diaries/{id}/media/{image_id} — detach a single DiaryImage. */
export async function detachDiaryMedia(diaryId: string, imageId: string): Promise<void> {
  const res = await call<unknown>("DELETE", `/api/v1/diaries/${diaryId}/media/${imageId}`, {
    expectBody: false,
  });
  if (!res.response.ok) throw await apiError(res, "DIARY_MEDIA_DETACH_FAILED");
}
